import { useEffect, useState } from "react";
import type { Business } from "@/types/directory";
import { queryBusinesses } from "@/services/directoryService";

/** The listing API caps `limit` at 50 — fetch the max it allows (sponsored/featured sort first). */
const PROMO_FETCH_LIMIT = 50;
/** Slightly larger sponsored pool so the panel can auto-rotate. */
const SPONSORED_POOL = 9;
/** Featured rows shown in a single column. */
const FEATURED_POOL = 6;

export interface DistrictPromos {
  sponsored: Business[];
  featured: Business[];
  loading: boolean;
}

/**
 * District-wide sponsored and featured placements.
 * Independent of taluk / rating so the promo band stays full on every page.
 * Pass an empty slug to skip fetching (non district/taluk pages).
 */
export function useDistrictPromos(districtSlug: string): DistrictPromos {
  const [sponsored, setSponsored] = useState<Business[]>([]);
  const [featured, setFeatured] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!districtSlug) {
      setSponsored([]);
      setFeatured([]);
      setLoading(false);
      return () => undefined;
    }
    let active = true;
    setLoading(true);
    queryBusinesses({
      district: districtSlug,
      sort: "relevance",
      page: 1,
      pageSize: PROMO_FETCH_LIMIT,
    })
      .then((response) => {
        if (!active) return;
        setSponsored(response.items.filter((b) => b.sponsored).slice(0, SPONSORED_POOL));
        setFeatured(response.items.filter((b) => b.featured && !b.sponsored).slice(0, FEATURED_POOL));
      })
      .catch(() => {
        if (active) {
          setSponsored([]);
          setFeatured([]);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [districtSlug]);

  return { sponsored, featured, loading };
}